import { RefObject, useEffect } from 'react';

import { isDefined } from '../../utils/variables';

type MouseButton = 'left' | 'middle' | 'right';

/**
 * Hooks up a mouse event to trigger a button click and calls a callback on mouse down.
 * If no ref is passed the hook will do nothing.
 */
export const usePointerEvent = (
  event: 'pointerup' | 'pointerdown',
  element: RefObject<HTMLElement> | undefined,
  button: MouseButton,
  callback?: (event: MouseEvent) => void,
  options?: { bubble: boolean },
) => {
  useEffect(() => {
    if (!isDefined(element)) {
      return;
    }

    const buttonMap: Record<MouseButton, number> = {
      left: 0,
      middle: 1,
      right: 2,
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (isDefined(element.current) && event.button === buttonMap[button] && callback) {
        callback(event);
      }
    };

    const currentElement = element.current;
    const bubble = options?.bubble ?? false;
    const eventName = event;

    if (currentElement) {
      currentElement.addEventListener(eventName, handleMouseDown, bubble);
    }

    return () => {
      if (currentElement) {
        currentElement.removeEventListener(eventName, handleMouseDown, bubble);
      }
    };
  }, [element, event, button, callback, options]);
};
